import { Button, Input, Text } from '@/components/ui';
import { DatePicker } from '@/components/ui/DatePicker';
import { Select, SelectOption } from '@/components/ui/Select';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMemo, useState, useEffect, useCallback } from 'react';
import { useUsers } from '@/hooks/useUsers';
import { useContractOptions } from '@/hooks/useContractOptions';
import { PlusIcon } from 'lucide-react';
import { TicketFormAppointment } from './TicketFormAppointment';
import { TicketFormAppointmentsTable } from './TicketFormAppointmentsTable';

export interface TicketAppointment {
  id?: string;
  date: Date;
  userId: string;
  workedTime: number;
  timeUnit: 'hours' | 'minutes';
  isDoubleDebited: boolean;
}

const ticketSchema = (t: any) =>
  z.object({
    number: z.string().min(1, t('tickets.form.numberRequired')),
    title: z.string().min(1, t('tickets.form.titleRequired')),
    userId: z.string().min(1, t('tickets.form.userRequired')),
    date: z.date({
      required_error: t('tickets.form.dateRequired'),
      invalid_type_error: t('tickets.form.invalidDate'),
    }),
    contractId: z.string().min(1, t('tickets.form.contractRequired')),
    appointments: z
      .array(
        z.object({
          date: z.date(),
          userId: z.string(),
          workedTime: z.number().min(1),
          timeUnit: z.enum(['hours', 'minutes']),
          isDoubleDebited: z.boolean(),
        })
      )
      .optional()
      .default([]),
  });

export type TicketFormData = z.infer<ReturnType<typeof ticketSchema>>;

export interface CreateTicketFormProps {
  mode: 'create';
  onSubmit: (data: TicketFormData & { appointments: TicketAppointment[] }) => Promise<void>;
  isLoading: boolean;
  onCancel: () => void;
}

export interface UpdateTicketFormProps {
  mode: 'update';
  onSubmit: (data: TicketFormData & { appointments: TicketAppointment[] }) => Promise<void>;
  isLoading: boolean;
  initialData: {
    number: string;
    title: string;
    userId: string;
    date: Date;
    contractId: string;
    appointments?: TicketAppointment[];
  };
  onCancel: () => void;
}

export type TicketFormProps = CreateTicketFormProps | UpdateTicketFormProps;

export function TicketForm(props: TicketFormProps) {
  const { mode, onSubmit, isLoading, onCancel } = props;
  const initialData = props.mode === 'update' ? props.initialData : undefined;

  const { t } = useTranslation();
  const { users } = useUsers({ limit: 100 }); // Fetch more users for select options
  const { contractOptions } = useContractOptions({ includeInactive: false }); // Only active contracts for tickets

  const isCreate = mode === 'create';
  const schema = ticketSchema(t);

  const [appointments, setAppointments] = useState<TicketAppointment[]>(
    initialData?.appointments || []
  );

  // Sync appointments with initialData when it changes (for update mode)
  useEffect(() => {
    if (mode === 'update' && initialData?.appointments) {
      setAppointments(initialData.appointments);
    }
  }, [mode, initialData?.appointments]);
  const [currentAppointment, setCurrentAppointment] = useState<Partial<TicketAppointment>>({
    timeUnit: 'hours',
    isDoubleDebited: false,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: isCreate
      ? {
          number: '',
          title: '',
          userId: '',
          date: undefined,
          contractId: '',
          appointments: [],
        }
      : {
          number: initialData?.number || '',
          title: initialData?.title || '',
          userId: initialData?.userId || '',
          date: initialData?.date,
          contractId: initialData?.contractId || '',
          appointments: initialData?.appointments || [],
        },
  });

  // Get user options from users (ADMIN and TIME_TRACKER only)
  const userOptions: SelectOption[] = useMemo(() => {
    return users
      .filter((user) => user.role === 'ADMIN' || user.role === 'TIME_TRACKER')
      .filter((user) => user.isActive)
      .map((user) => ({
        value: user.id,
        content: user.name,
      }));
  }, [users]);

  const addAppointment = useCallback(() => {
    if (
      currentAppointment.date &&
      currentAppointment.userId &&
      currentAppointment.workedTime &&
      currentAppointment.workedTime > 0
    ) {
      const appointment: TicketAppointment = {
        date: currentAppointment.date,
        userId: currentAppointment.userId,
        workedTime: currentAppointment.workedTime,
        timeUnit: currentAppointment.timeUnit || 'hours',
        isDoubleDebited: currentAppointment.isDoubleDebited || false,
      };

      setAppointments((prev) => [...prev, appointment]);
      setCurrentAppointment({
        timeUnit: 'hours',
        isDoubleDebited: false,
      });
    }
  }, [currentAppointment]);

  const removeAppointment = useCallback((index: number) => {
    setAppointments((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleFormSubmit = async (data: any) => {
    // Convert appointments to API format (workedTime in minutes)
    const appointmentsForAPI = appointments.map((appointment) => ({
      ...appointment,
      workedTime:
        appointment.timeUnit === 'hours' ? appointment.workedTime * 60 : appointment.workedTime,
    }));

    await onSubmit({
      ...data,
      appointments: appointmentsForAPI,
    });
  };

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="gap-md flex flex-col">
      <Input
        {...register('number')}
        label={t('tickets.form.number')}
        placeholder={t('tickets.form.numberPlaceholder')}
        error={errors.number?.message}
        disabled={isLoading}
      />
      <Select
        options={contractOptions}
        label={t('tickets.form.contract')}
        placeholder={t('tickets.form.contractPlaceholder')}
        value={watch('contractId')}
        onValueChange={(value) => setValue('contractId', value)}
        error={errors.contractId?.message}
        disabled={isLoading}
      />
      <Input
        {...register('title')}
        label={t('tickets.form.title')}
        placeholder={t('tickets.form.titlePlaceholder')}
        error={errors.title?.message}
        disabled={isLoading}
      />
      <div className="gap-md grid grid-cols-2">
        <Select
          options={userOptions}
          label={t('tickets.form.user')}
          placeholder={t('tickets.form.userPlaceholder')}
          value={watch('userId')}
          onValueChange={(value) => setValue('userId', value)}
          error={errors.userId?.message}
          disabled={isLoading}
        />
        <DatePicker
          label={t('tickets.form.date')}
          placeholder={t('tickets.form.datePlaceholder')}
          value={watch('date')}
          onChange={(date) => setValue('date', date as Date)}
          error={errors.date?.message}
          disabled={isLoading}
        />
      </div>
      <hr className="border-neutral-subtle" />
      <div className="gap-md flex items-center justify-between">
        <Text variant="label" strong>
          Apontamentos
        </Text>

        <Button type="button" variant="primary" size="sm" onClick={addAppointment}>
          <PlusIcon size={14} />
          Adicionar
        </Button>
      </div>
      <TicketFormAppointment
        appointment={currentAppointment}
        onAppointmentChange={setCurrentAppointment}
      />
      <TicketFormAppointmentsTable
        appointments={appointments}
        onRemoveAppointment={removeAppointment}
      />

      <footer className="gap-sm mt-2xl flex self-end">
        <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" loading={isLoading}>
          {t(isCreate ? 'common.create' : 'common.save')}
        </Button>
      </footer>
    </form>
  );
}
